import { Anchor, Badge, Group, Select, Stack, Table, TextInput, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useState } from 'react';
import { Link } from 'react-router';

import { useRoles } from '../../features/roles';
import { useUsersList } from '../../features/users';
import { PROVIDER_LABEL, formatDateTime } from '../../shared/format';
import { CursorPager, PageFallback, hasItems, useCursorPager } from '../../shared/ui';

const STATUSES = [
  { value: 'active', label: 'Активен' },
  { value: 'suspended', label: 'Заблокирован' },
];

export function UsersListPage() {
  // Роли — данные, а не константа (AD-A6): фильтр показывает то, что отдал каталог. Пока
  // каталог не прочитан или недоступен, фильтр пуст и выключен — список от этого работает.
  const roles = useRoles();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [q] = useDebouncedValue(search.trim(), 300);
  const filters = { q: q || undefined, role: role ?? undefined, status: status ?? undefined };
  const pager = useCursorPager(JSON.stringify(filters));
  const page = useUsersList(filters, pager.cursor);

  return (
    <Stack gap="md">
      <Title order={2}>Пользователи</Title>
      <Group align="flex-end">
        <TextInput
          label="Поиск"
          placeholder="Имя или email"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          w={280}
        />
        <Select
          label="Роль"
          data={(roles.data ?? []).map((item) => ({
            value: item.slug,
            label: `${item.displayName} (${item.slug})`,
          }))}
          placeholder={roles.isError ? 'Каталог ролей недоступен' : 'Любая'}
          disabled={roles.isPending || roles.isError}
          value={role}
          onChange={setRole}
          clearable
          w={220}
        />
        <Select
          label="Статус"
          data={STATUSES}
          value={status}
          onChange={setStatus}
          clearable
          w={180}
        />
      </Group>

      <PageFallback query={page} empty="Никого не нашлось." />
      {hasItems(page) ? (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Имя</Table.Th>
              <Table.Th>Вход</Table.Th>
              <Table.Th>Роли</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Создан</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {page.data?.items.map((account) => (
              <Table.Tr key={account.id}>
                <Table.Td>
                  <Anchor component={Link} to={`/users/${account.id}`}>
                    {account.displayName ?? '— без имени —'}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  {account.providers
                    .map((provider) => PROVIDER_LABEL[provider] ?? provider)
                    .join(', ')}
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {account.roles.map((slug) => (
                      <Badge key={slug} variant="light">
                        {slug}
                      </Badge>
                    ))}
                  </Group>
                </Table.Td>
                <Table.Td>{account.status}</Table.Td>
                <Table.Td>{formatDateTime(account.createdAt)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : null}
      <CursorPager query={page} pager={pager} />
    </Stack>
  );
}
